import { use } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AddCircleOutlineRounded, Link } from '@mui/icons-material';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import BreadCrumbTooltip from 'shared/components/bread-crumb/bread-crumb-tooltip/bread-crumb-tooltip';
import Divider from 'shared/components/divider/divider';
import SelectField from 'shared/components/formFields/select-field';

import styles from 'features/systems/ip-unit/components/ip-unit-io-functionality/ip-unit-io-functionality.module.scss';
import CreateIPUnitDeviceCommand from 'features/systems/ip-unit/domain/models/create-ip-unit-device-command';
import { IPUnitEventType } from 'features/systems/ip-unit/domain/models/ip-unit-event-type';
import IPUnitProviderContext from 'features/systems/ip-unit/providers/contexts/ip-unit-provider-context';

const IPUnitIOFunctionality = (): React.JSX.Element => {
  const { t } = useTranslation('ipUnit');
  const { t: tIOEventTypes } = useTranslation('ioEventTypes');

  const { control } = useFormContext();

  const {
    viewingMode,
    openDeviceDetailsByExternalId,
    ioEntryHasDevice,
    ioEntryHasLinkedDevice,
    getIOEntryDeviceBreadCrumbs,
    createIPUnitDevice,
    selectedSystem,
    readIPUnitConfigurationData,
  } = use(IPUnitProviderContext);

  const renderTitle = (index: number) => (
    <div className={styles.titleContainer}>
      <Typography
        variant="body2"
        className={`${viewingMode === 'viewing' && ioEntryHasDevice(index) ? styles.clickable : ''}`}
        onClick={() => viewingMode === 'viewing' && ioEntryHasDevice(index) && openDeviceDetailsByExternalId(`ingang_${index}`)}
      >
        {t(`ioFunctionality.entry`, { index })}:
      </Typography>

      {ioEntryHasLinkedDevice(index) && (
        <div className={styles.linkButton}>
          <BreadCrumbTooltip allElementNames={getIOEntryDeviceBreadCrumbs(index)}>
            <Link sx={{ fontSize: 18 }} />
          </BreadCrumbTooltip>
        </div>
      )}
    </div>
  );

  const renderEditingModeView = (index: number) => {
    const arrayIndex = index - 1;
    const fieldNameLow = `events.${arrayIndex}.eventTypeLow`;
    const fieldNameHigh = `events.${arrayIndex}.eventTypeHigh`;

    return (
      <div className={styles.selectContainer}>
        <Controller
          name={fieldNameLow}
          control={control}
          render={({ field }) => {
            const controlledValue = field.value === undefined ? IPUnitEventType.NoEvent : field.value;
            return (
              <SelectField
                {...field}
                value={controlledValue}
                onChange={(e) => field.onChange(e.target.value)}
                id={`ioEntry-${index}-low`}
                label={t('ioFunctionality.low')}
                variant="outlined"
                className={styles.selectEvent}
              >
                {Object.values(IPUnitEventType).map((option) => {
                  return (
                    <MenuItem key={`eventTypes-${option}-low-${index}`} value={option}>
                      {tIOEventTypes(`${option}`)}
                    </MenuItem>
                  );
                })}
              </SelectField>
            );
          }}
        />
        <Controller
          name={fieldNameHigh}
          control={control}
          render={({ field }) => {
            const controlledValue = field.value === undefined ? IPUnitEventType.NoEvent : field.value;
            return (
              <SelectField
                {...field}
                value={controlledValue}
                onChange={(e) => field.onChange(e.target.value)}
                id={`ioEntry-${index}-high`}
                label={t('ioFunctionality.high')}
                variant="outlined"
                className={styles.selectEvent}
              >
                {Object.values(IPUnitEventType).map((option) => {
                  return (
                    <MenuItem key={`eventTypes-${option}-high-${index}`} value={option}>
                      {tIOEventTypes(`${option}`)}
                    </MenuItem>
                  );
                })}
              </SelectField>
            );
          }}
        />
      </div>
    );
  };

  const renderViewingModeView = (index: number) => {
    const currentFormEventLow = readIPUnitConfigurationData?.events?.find((event) => event.number === index)?.eventTypeLow;
    const currentFormEventHigh = readIPUnitConfigurationData?.events?.find((event) => event.number === index)?.eventTypeHigh;

    return (
      <div className={styles.highLowContainer}>
        <div className={styles.highLowLabels}>
          <Typography variant="subtitle1" sx={{ lineHeight: '16px' }}>
            {t('ioFunctionality.low')}
            {':'}
          </Typography>
          <Typography variant="subtitle1" sx={{ lineHeight: '16px' }}>
            {t('ioFunctionality.high')}
            {':'}
          </Typography>
        </div>
        <div className={styles.highLowValues}>
          <Typography
            variant="subtitle1"
            sx={{
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {currentFormEventLow ? tIOEventTypes(`${currentFormEventLow}`) : tIOEventTypes('NoEvent')}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              lineHeight: '16px',
              whiteSpace: 'nowrap',
            }}
          >
            {currentFormEventHigh ? tIOEventTypes(`${currentFormEventHigh}`) : tIOEventTypes('NoEvent')}
          </Typography>
        </div>
      </div>
    );
  };

  const renderActionButton = (index: number) =>
    viewingMode === 'viewing' &&
    !ioEntryHasDevice(index) && (
      <Button
        startIcon={<AddCircleOutlineRounded color="primary" />}
        onClick={() => {
          if (selectedSystem?.id) {
            const command = {
              systemId: selectedSystem.id,
              number: index,
              isSubUnit: true,
            } as CreateIPUnitDeviceCommand;
            createIPUnitDevice(command);
          }
        }}
      >
        {t('ioFunctionality.createDevice')}
      </Button>
    );

  const renderInput = (index: number) => {
    return (
      <div className={styles.row}>
        {renderTitle(index)}
        <div className={styles.inputContainer}>
          {viewingMode === 'editing' ? renderEditingModeView(index) : renderViewingModeView(index)}
        </div>
        {renderActionButton(index)}
      </div>
    );
  };

  return readIPUnitConfigurationData?.ioFunctionalityAvailable ? (
    <div className={styles.container}>
      <Typography className={styles.title} variant="h5">
        {t('ioFunctionality.title')}
      </Typography>
      <Stack gap={1} direction={'column'}>
        {renderInput(1)}
        {renderInput(2)}
        {renderInput(3)}
        {renderInput(4)}
      </Stack>
      <Divider className={styles.divider} />
    </div>
  ) : (
    <></>
  );
};

export default IPUnitIOFunctionality;
